import { useTranslation } from '../i18n';

import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Input, Button, message } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useAuth } from '../hooks/useAuth';
import AuthLayout from '../components/AuthLayout';
import InlineCaptcha from '../components/InlineCaptcha';



const ResetPassword = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const { resetPassword, confirmResetPassword } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');

    const onRequestReset = async (values) => {
        try {
            setLoading(true);
            // AuthContext 已统一弹出中性的成功/失败提示
            await resetPassword(values.account.trim());
        } catch (error) {
            message.error(t('Password reset request failed: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setLoading(false);
        }
    };

    const onConfirmReset = async (values) => {
        try {
            setLoading(true);
            const result = await confirmResetPassword(token, values.password);

            if (result.success) {
                message.success(t('Password reset. Please sign in with your new password.'));
                navigate('/login');
            }
        } catch (error) {
            message.error(t('Failed to reset password: {{value}}', { value: error.message || t('Unknown error') }));
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout title={t('Reset password')} subtitle={t('We will send a reset link to your email address')}>

                {!token ? (
                    // 请求重置密码表单
                    <Form
                        name="reset-request"
                        initialValues={{}}
                        onFinish={onRequestReset}
                        size="large"
                    >
                        <Form.Item
                            name="account"
                            rules={[{ required: true, whitespace: true, message: t('Please enter your username or email') }]}
                        >
                            <Input prefix={<UserOutlined />} placeholder={t('Username or email')} autoComplete="username" />
                        </Form.Item>

                        <InlineCaptcha action="reset" />
                        <Form.Item>
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                                {t('Send reset link')}
                            </Button>
                            <div style={{ marginTop: 16, textAlign: 'center' }}>
                                {t('Remember your password? ')}<Link to="/login">{t('Back to sign in')}</Link>
                            </div>
                        </Form.Item>
                    </Form>
                ) : (
                    // 确认重置密码表单
                    <Form
                        name="reset-confirm"
                        initialValues={{}}
                        onFinish={onConfirmReset}
                        size="large"
                    >
                        <Form.Item
                            name="password"
                            rules={[
                                { required: true, message: t('Please enter a new password!') },
                                { min: 8, message: t('Password must be at least 8 characters!') },
                                { pattern: /^(?=.*[A-Za-z])(?=.*\d)/, message: t('Password must contain both letters and numbers!') }
                            ]}
                            hasFeedback
                            extra={t('At least 8 characters, including both letters and numbers')}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t('New password')} />
                        </Form.Item>

                        <Form.Item
                            name="confirm"
                            dependencies={['password']}
                            hasFeedback
                            rules={[
                                { required: true, message: t('Please confirm your new password!') },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject(new Error(t('Passwords do not match!')));
                                    },
                                }),
                            ]}
                        >
                            <Input.Password prefix={<LockOutlined />} placeholder={t('Confirm new password')} />
                        </Form.Item>

                        <InlineCaptcha action="reset" />
                        <Form.Item>
                            <Button type="primary" htmlType="submit" style={{ width: '100%' }} loading={loading}>
                                {t('Reset password')}
                            </Button>
                            <div style={{ marginTop: 16, textAlign: 'center' }}>
                                <Link to="/login">{t('Back to sign in')}</Link>
                            </div>
                        </Form.Item>
                    </Form>
                )}
        </AuthLayout>
    );
};

export default ResetPassword;
